/** Daily booking summary: today's active portfolio by state and carrier. */
import {
  allBookingsHref,
  carrierBookingsHref,
  stateBookingsHref,
} from "@/features/feed/feedLinks";
import { formatDimensionValue } from "@/lib/formatDimensionValue";
import type { DailyBookingSummaryPayload } from "@/types/feed";

import { compareScale, plural } from "../formatInsight";
import { explainerFor } from "../insightCopy";
import {
  ChangeRow,
  CtaLink,
  EntitySection,
  InsightShell,
  MetricGrid,
  type MetricItem,
} from "../primitives";

export function DailyBookingSummaryView({
  payload,
}: {
  payload: DailyBookingSummaryPayload;
}) {
  const data = payload.data ?? {};
  const byState = data.by_state ?? [];
  const byCarrier = data.by_carrier ?? [];
  const stateMax = compareScale(byState.map((row) => row.count));
  const carrierMax = compareScale(byCarrier.map((row) => row.count));

  const metricItems: MetricItem[] =
    typeof data.total_bookings === "number"
      ? [{ label: "Active bookings", value: String(data.total_bookings) }]
      : [];

  if (metricItems.length === 0 && byState.length === 0 && byCarrier.length === 0) {
    return null;
  }

  return (
    <InsightShell explainer={explainerFor(payload as unknown as Record<string, unknown>)}>
      {metricItems.length > 0 && <MetricGrid items={metricItems} />}
      {byState.length > 0 && (
        <EntitySection title="By state">
          {byState.map((s) => (
            <ChangeRow
              key={s.state}
              name={formatDimensionValue("state", s.state)}
              href={stateBookingsHref(s.state)}
              primary={String(s.count)}
              subtitle={plural(s.count, "booking")}
              bar={{ type: "share", value: s.count, max: stateMax }}
            />
          ))}
        </EntitySection>
      )}
      {byCarrier.length > 0 && (
        <EntitySection title="By carrier">
          {byCarrier.map((c) => (
            <ChangeRow
              key={c.carrier}
              name={c.carrier}
              href={carrierBookingsHref(c.carrier)}
              primary={String(c.count)}
              bar={{ type: "share", value: c.count, max: carrierMax }}
            />
          ))}
        </EntitySection>
      )}
      <CtaLink to={allBookingsHref()}>View all bookings</CtaLink>
    </InsightShell>
  );
}
